import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getUpcomingPaymentsSummary } from "@/lib/api";
import { formatAdminBusinessEt } from "@/lib/admin-datetime";
import { formatCurrency } from "./reconciliation-shared";

export function UpcomingPaymentsTab() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [upcomingSummary, setUpcomingSummary] = useState<Awaited<
    ReturnType<typeof getUpcomingPaymentsSummary>
  >["data"] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUpcomingPaymentsSummary({ includeErrors: false });
      setUpcomingSummary(response.data);
    } catch {
      toast.error("Failed to load upcoming payments summary");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const formatDate = (dateString: string) => formatAdminBusinessEt(dateString);

  return (
    <div className="pr-card">
      <div className="pr-card-head">
        <div>
          <h2>Upcoming Payments</h2>
          <p>Overview of scheduled payments across all subscriptions</p>
        </div>
      </div>
      <div style={{ padding: "20px 24px" }}>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : upcomingSummary ? (
          <div className="space-y-6">
            <div className="pr-analytics-cards" style={{ padding: 0 }}>
              <div className="pr-metric pr-tone-blue">
                <div className="pr-metric-top">
                  <div className="pr-metric-title">Total Upcoming</div>
                </div>
                <div className="pr-metric-number">{upcomingSummary.totalUpcoming}</div>
              </div>
              <div className="pr-metric pr-tone-green">
                <div className="pr-metric-top">
                  <div className="pr-metric-title">Total Amount</div>
                </div>
                <div className="pr-metric-number">{formatCurrency(upcomingSummary.totalAmount)}</div>
              </div>
              <div className="pr-metric pr-tone-orange">
                <div className="pr-metric-top">
                  <div className="pr-metric-title">Next 7 Days</div>
                </div>
                <div className="pr-metric-number">{formatCurrency(upcomingSummary.next7Days.amount)}</div>
                <div className="pr-metric-note">{upcomingSummary.next7Days.count} Payment{upcomingSummary.next7Days.count === 1 ? "" : "s"}</div>
              </div>
              <div className="pr-metric pr-tone-purple">
                <div className="pr-metric-top">
                  <div className="pr-metric-title">Next 30 Days</div>
                </div>
                <div className="pr-metric-number">{formatCurrency(upcomingSummary.next30Days.amount)}</div>
                <div className="pr-metric-note">{upcomingSummary.next30Days.count} Payment{upcomingSummary.next30Days.count === 1 ? "" : "s"}</div>
              </div>
            </div>

            {upcomingSummary.students && upcomingSummary.students.length > 0 && (
              <div className="pr-table-wrap" style={{ padding: 0 }}>
                <div className="pr-toolbar">
                  <strong>Per-Student Payment Details</strong>
                </div>
                <table className="pr-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Program</th>
                      <th>Payment Progress</th>
                      <th>Amount Paid</th>
                      <th>Amount Remaining</th>
                      <th>Next Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingSummary.students.flatMap((student) =>
                      student.subscriptions.map((subscription, subIndex) => (
                        <tr
                          key={`${student.studentId}-${subscription.subscriptionId || subIndex}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => navigate(`/users?studentId=${student.studentId}`)}
                        >
                          {subIndex === 0 ? (
                            <>
                              <td className="pr-student-cell">
                                <strong>{student.fullName}</strong>
                                <span>
                                  {student.email}
                                  <br />
                                  Account: {student.accountId}
                                </span>
                              </td>
                              <td>{subscription.programName}</td>
                              <td>
                                {student.paymentSummary.totalCompletedPayments} /{" "}
                                {student.paymentSummary.totalCompletedPayments +
                                  student.paymentSummary.totalRemainingPayments}{" "}
                                total payments
                              </td>
                              <td>
                                <strong>{formatCurrency(student.paymentSummary.totalAmountPaid)}</strong>
                              </td>
                              <td>
                                <strong>{formatCurrency(student.paymentSummary.totalAmountRemaining)}</strong>
                              </td>
                              <td>
                                <strong>{formatCurrency(student.nextPayment.amount)}</strong>
                                <div style={{ fontSize: 12, color: "#64748b" }}>
                                  {formatDate(student.nextPayment.date)}
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>
                                <span style={{ marginLeft: 16, color: "#64748b" }}>↳</span>
                              </td>
                              <td>{subscription.programName}</td>
                              <td>
                                {subscription.completedPayments} / {subscription.numberOfPayments} payments
                              </td>
                              <td>{formatCurrency(subscription.amountPaid)}</td>
                              <td>{formatCurrency(subscription.amountRemaining)}</td>
                              <td>
                                {formatCurrency(
                                  subscription.amountRemaining / Math.max(subscription.remainingPayments, 1)
                                )}
                                <div style={{ fontSize: 12, color: "#64748b" }}>
                                  {formatDate(subscription.nextPaymentDate)}
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="pr-empty-state">
            <strong>No upcoming payments data</strong>
          </div>
        )}
      </div>
    </div>
  );
}
